import api from '../api';

export type VerificationJobProgress = {
  phase?: string;
  current?: number;
  total?: number;
};

export type VerificationJobStatus = {
  job_id: string;
  status: 'pending' | 'waiting' | 'active' | 'delayed' | 'completed' | 'failed' | string;
  progress?: VerificationJobProgress | number | null;
  scope?: string;
  scope_id?: number | string;
  verification?: any;
  error?: string;
};

export interface PollOptions {
  intervalMs?: number;
  maxWaitMs?: number;
  onProgress?: (status: VerificationJobStatus) => void;
}

export function describeProgress(progress: VerificationJobProgress | number | null | undefined): string {
  if (!progress || typeof progress === 'number') return 'Verifying...';
  if (progress.phase === 'extracting_claims') return 'Extracting claims...';
  if (progress.phase === 'verifying_claims') {
    const cur = progress.current ?? 0;
    const tot = progress.total ?? 0;
    return tot > 0 ? `Verifying claim ${cur} of ${tot}...` : 'Verifying claims...';
  }
  return 'Verifying...';
}

export async function pollVerificationJob(jobId: string, opts: PollOptions = {}): Promise<VerificationJobStatus> {
  const intervalMs = opts.intervalMs ?? 3000;
  const maxWaitMs = opts.maxWaitMs ?? 30 * 60 * 1000;
  const startedAt = Date.now();
  while (true) {
    if (Date.now() - startedAt > maxWaitMs) {
      throw new Error('Verification job timed out');
    }
    const res = await api.get(`/api/analysis/verification-job/${jobId}`);
    const status = res.data as VerificationJobStatus;
    if (opts.onProgress) opts.onProgress(status);
    if (status.status === 'completed') return status;
    if (status.status === 'failed') throw new Error(status.error || 'Verification failed');
    await new Promise(r => setTimeout(r, intervalMs));
  }
}
